"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { DevActivityBarChart } from "@/components/DevActivityBarChart";
import { CommitHeatmap } from "@/components/CommitHeatmap";
import { DevScoreRadial } from "@/components/DevScoreRadial";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RepoStats {
  repoFullName: string;
  stars: number;
  forks: number;
  openIssues: number;
  language: string | null;
  lastCommitAt: string | Date | null;
}
interface DevSnapshot {
  commits: number;
  pullRequests: number;
  contributors: number;
  date: string | Date;
}
interface TokenDevData {
  id: string;
  name: string;
  symbol: string;
  slug: string;
  imageUrl: string | null;
  githubOrg: string | null;
  devActivityScore: number;
  totalCommits30d: number;
  totalPRs30d: number;
  totalIssues30d: number;
  activeDevs30d: number;
  currentPrice: number | null;
  priceChangePercent24h: number | null;
  marketCapRank: number | null;
  githubStats: RepoStats[];
  devActivityHistory: DevSnapshot[];
}
interface DevActivityClientProps {
  tokens: TokenDevData[];
}
type SortKey = "score" | "commits" | "prs" | "devs";
type ViewMode = "grid" | "table" | "chart";

// ---------------------------------------------------------------------------
// Language color map
// ---------------------------------------------------------------------------

const LANG_COLORS: Record<string, string> = {
  Rust: "#dea584",
  Go: "#00ADD8",
  TypeScript: "#3178c6",
  JavaScript: "#f1e05a",
  Solidity: "#AA6746",
  Python: "#3572A5",
  "C++": "#f34b7d",
  C: "#555555",
  Haskell: "#5e5086",
  Move: "#4a137a",
  Cairo: "#ff4600",
  OCaml: "#3be133",
  Java: "#b07219",
  Kotlin: "#A97BFF"
};
function getLangColor(lang: string | null): string {
  if (!lang) return "#6b7280";
  return LANG_COLORS[lang] || "#6b7280";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DevActivityClient(t0) {
  const $ = _c(77);
  const {
    tokens
  } = t0;
  const [sortBy, setSortBy] = useState("score");
  const [viewMode, setViewMode] = useState("grid");
  const [selectedToken, setSelectedToken] = useState(null);
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== selectedToken?.id || $[1] !== sortBy || $[2] !== tokens || $[3] !== viewMode) {
    let t10;
    if ($[13] !== sortBy) {
      t10 = (a, b) => {
        switch (sortBy) {
          case "commits":
            {
              return b.totalCommits30d - a.totalCommits30d;
            }
          case "prs":
            {
              return b.totalPRs30d - a.totalPRs30d;
            }
          case "devs":
            {
              return b.activeDevs30d - a.activeDevs30d;
            }
          default:
            {
              return b.devActivityScore - a.devActivityScore;
            }
        }
      };
      $[13] = sortBy;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    const sorted = [...tokens].sort(t10);
    const chartData = sorted.slice(0, 20).map(_temp);
    let t11;
    let t12;
    let t13;
    let t14;
    let t15;
    if ($[15] !== tokens) {
      const totalCommits = tokens.reduce(_temp2, 0);
      const totalPRs = tokens.reduce(_temp3, 0);
      new Set(tokens.flatMap(_temp5));
      const totalRepos = tokens.reduce(_temp6, 0);
      const langCounts = new Map();
      for (const t_4 of tokens) {
        for (const r_0 of t_4.githubStats) {
          if (r_0.language) {
            langCounts.set(r_0.language, (langCounts.get(r_0.language) || 0) + 1);
          }
        }
      }
      const topLangs = [...langCounts.entries()].sort(_temp7).slice(0, 10);
      const langTotal = topLangs.reduce(_temp8, 0);
      t1 = "max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8";
      let t16;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3">Developer Activity</h1>;
        $[24] = t16;
      } else {
        t16 = $[24];
      }
      if ($[25] !== tokens.length) {
        t2 = <div className="mb-8 sm:mb-10">{t16}<p className="text-gray-400 text-base sm:text-lg max-w-2xl">Real GitHub data from core repositories across {tokens.length} blockchain projects. Commits, PRs, contributors, and code changes — updated daily.</p></div>;
        $[25] = tokens.length;
        $[26] = t2;
      } else {
        t2 = $[26];
      }
      const t17 = totalCommits.toLocaleString();
      let t18;
      if ($[27] !== t17) {
        t18 = {
          label: "Total Commits (30d)",
          value: t17,
          icon: "M12 6v6m0 0v6m0-6h6m-6 0H6"
        };
        $[27] = t17;
        $[28] = t18;
      } else {
        t18 = $[28];
      }
      const t19 = totalPRs.toLocaleString();
      let t20;
      if ($[29] !== t19) {
        t20 = {
          label: "Pull Requests (30d)",
          value: t19,
          icon: "M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
        };
        $[29] = t19;
        $[30] = t20;
      } else {
        t20 = $[30];
      }
      const t21 = tokens.reduce(_temp9, 0).toLocaleString();
      let t22;
      if ($[31] !== t21) {
        t22 = {
          label: "Core Contributors (30d)",
          value: t21,
          icon: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
        };
        $[31] = t21;
        $[32] = t22;
      } else {
        t22 = $[32];
      }
      const t23 = totalRepos.toString();
      let t24;
      if ($[33] !== t23) {
        t24 = {
          label: "Tracked Repos",
          value: t23,
          icon: "M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
        };
        $[33] = t23;
        $[34] = t24;
      } else {
        t24 = $[34];
      }
      if ($[35] !== t18 || $[36] !== t20 || $[37] !== t22 || $[38] !== t24) {
        t3 = <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">{[t18, t20, t22, t24].map(_temp10)}</div>;
        $[35] = t18;
        $[36] = t20;
        $[37] = t22;
        $[38] = t24;
        $[39] = t3;
      } else {
        t3 = $[39];
      }
      t13 = "rounded-xl border border-[hsl(var(--border))] bg-white/[0.02] p-4 sm:p-6 mb-8 sm:mb-10";
      if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = <h2 className="text-base sm:text-lg font-semibold mb-4">Language Distribution</h2>;
        $[40] = t14;
      } else {
        t14 = $[40];
      }
      let t25;
      if ($[41] !== langTotal) {
        t25 = t26 => {
          const [lang, count] = t26;
          return <div key={lang} style={{
            width: `${count / langTotal * 100}%`,
            backgroundColor: getLangColor(lang)
          }} title={`${lang}: ${count} repos`} />;
        };
        $[41] = langTotal;
        $[42] = t25;
      } else {
        t25 = $[42];
      }
      t15 = <div className="flex rounded-full h-3 overflow-hidden mb-4">{topLangs.map(t25)}</div>;
      t11 = "flex flex-wrap gap-x-4 gap-y-2";
      let t26;
      if ($[43] !== langTotal) {
        t26 = t27 => {
          const [lang_0, count_0] = t27;
          return <div key={lang_0} className="flex items-center gap-1.5 text-xs sm:text-sm"><span className="w-3 h-3 shrink-0 rounded-full" style={{
              backgroundColor: getLangColor(lang_0)
            }} /><span className="text-gray-300">{lang_0}</span><span className="text-gray-600">{(count_0 / langTotal * 100).toFixed(1)}%</span></div>;
        };
        $[43] = langTotal;
        $[44] = t26;
      } else {
        t26 = $[44];
      }
      t12 = topLangs.map(t26);
      $[15] = tokens;
      $[16] = t1;
      $[17] = t11;
      $[18] = t12;
      $[19] = t13;
      $[20] = t14;
      $[21] = t15;
      $[22] = t2;
      $[23] = t3;
    } else {
      t1 = $[16];
      t11 = $[17];
      t12 = $[18];
      t13 = $[19];
      t14 = $[20];
      t15 = $[21];
      t2 = $[22];
      t3 = $[23];
    }
    let t16;
    if ($[45] !== t11 || $[46] !== t12) {
      t16 = <div className={t11}>{t12}</div>;
      $[45] = t11;
      $[46] = t12;
      $[47] = t16;
    } else {
      t16 = $[47];
    }
    if ($[48] !== t13 || $[49] !== t14 || $[50] !== t15 || $[51] !== t16) {
      t4 = <div className={t13}>{t14}{t15}{t16}</div>;
      $[48] = t13;
      $[49] = t14;
      $[50] = t15;
      $[51] = t16;
      $[52] = t4;
    } else {
      t4 = $[52];
    }
    let t17;
    let t18;
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <h2 className="text-base sm:text-lg font-semibold mb-2">Top 20 Projects by Developer Activity</h2>;
      t18 = <p className="text-sm text-gray-500 mb-4">Commits, merged PRs, and active developers in the last 30 days</p>;
      $[53] = t17;
      $[54] = t18;
    } else {
      t17 = $[53];
      t18 = $[54];
    }
    t5 = <div className="rounded-xl border border-[hsl(var(--border))] bg-white/[0.02] p-4 sm:p-6 mb-8 sm:mb-10">{t17}{t18}<DevActivityBarChart data={chartData} /></div>;
    let t19;
    let t20;
    if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = <span className="text-sm text-gray-500 w-full sm:w-auto">Sort by:</span>;
      t20 = ["score", "commits", "prs", "devs"];
      $[55] = t19;
      $[56] = t20;
    } else {
      t19 = $[55];
      t20 = $[56];
    }
    let t21;
    if ($[57] !== sortBy) {
      t21 = <div className="flex flex-wrap items-center gap-2">{t19}{(t20 as SortKey[]).map(key => <button key={key} onClick={() => setSortBy(key)} className={`min-h-[44px] px-3.5 py-2 rounded-lg text-xs font-medium transition-colors ${sortBy === key ? "bg-blue-600 text-white" : "bg-white/5 text-gray-400 hover:text-white hover:bg-white/10"}`}>{key === "score" ? "Dev Score" : key === "commits" ? "Commits" : key === "prs" ? "PRs" : "Active Devs"}</button>)}</div>;
      $[57] = sortBy;
      $[58] = t21;
    } else {
      t21 = $[58];
    }
    let t22;
    if ($[59] !== viewMode) {
      t22 = <div className="flex items-center gap-1 border border-[hsl(var(--border))] rounded-lg overflow-hidden">{(["grid", "table", "chart"] as ViewMode[]).map(mode => <button key={mode} onClick={() => setViewMode(mode)} className={`min-h-[44px] px-4 py-2 text-xs font-medium transition-colors ${viewMode === mode ? "bg-blue-600 text-white" : "text-gray-400 hover:text-white hover:bg-white/5"}`}>{mode.charAt(0).toUpperCase() + mode.slice(1)}</button>)}</div>;
      $[59] = viewMode;
      $[60] = t22;
    } else {
      t22 = $[60];
    }
    if ($[61] !== t21 || $[62] !== t22) {
      t6 = <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">{t21}{t22}</div>;
      $[61] = t21;
      $[62] = t22;
      $[63] = t6;
    } else {
      t6 = $[63];
    }
    t7 = viewMode === "grid" && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{sorted.map(token => <button key={token.id} onClick={() => setSelectedToken(selectedToken?.id === token.id ? null : token)} className={`text-left p-4 sm:p-5 rounded-xl border transition-all ${selectedToken?.id === token.id ? "border-blue-500/50 bg-blue-500/5" : "border-[hsl(var(--border))] bg-white/[0.02] hover:bg-white/[0.04] hover:border-[hsl(var(--border))]/80"}`}><div className="flex items-center gap-3 mb-4">{token.imageUrl ? <Image src={token.imageUrl} alt={token.name} width={36} height={36} className="rounded-full" /> : <div className="w-9 h-9 rounded-full bg-gray-700 flex items-center justify-center text-sm font-bold">{token.symbol.charAt(0)}</div>}<div className="flex-1 min-w-0"><h3 className="font-semibold text-white truncate">{token.name}</h3><p className="text-xs text-gray-500">{token.githubOrg}</p></div><DevScoreRadial score={token.devActivityScore} size={56} /></div><div className="grid grid-cols-3 gap-3 text-center"><div className="bg-[hsl(230,15%,8%)] rounded-lg p-2.5"><p className="text-lg font-bold text-blue-400">{token.totalCommits30d.toLocaleString()}</p><p className="text-[11px] text-gray-500 uppercase">Commits</p></div><div className="bg-[hsl(230,15%,8%)] rounded-lg p-2.5"><p className="text-lg font-bold text-cyan-400">{token.totalPRs30d.toLocaleString()}</p><p className="text-[11px] text-gray-500 uppercase">PRs</p></div><div className="bg-[hsl(230,15%,8%)] rounded-lg p-2.5"><p className="text-lg font-bold text-purple-400">{token.activeDevs30d.toLocaleString()}</p><p className="text-[11px] text-gray-500 uppercase">Devs</p></div></div>{token.githubStats.length > 0 && <div className="flex flex-wrap gap-1.5 mt-3">{[...new Set(token.githubStats.map(_temp11).filter(Boolean))].slice(0, 4).map(_temp12)}</div>}</button>)}</div>;
    t8 = viewMode === "table" && <div className="overflow-x-auto rounded-xl border border-[hsl(var(--border))] bg-white/[0.02]"><table className="w-full text-xs sm:text-sm"><thead><tr className="border-b border-[hsl(var(--border))] text-gray-500 text-left"><th className="px-2 sm:px-4 py-3 font-medium w-8 sm:w-10">#</th><th className="px-2 sm:px-4 py-3 font-medium max-w-[150px] sm:max-w-none">Project</th><th className="px-2 sm:px-4 py-3 font-medium text-right">Score</th><th className="px-2 sm:px-4 py-3 font-medium text-right">Commits</th><th className="px-2 sm:px-4 py-3 font-medium text-right hidden sm:table-cell">PRs</th><th className="px-2 sm:px-4 py-3 font-medium text-right hidden md:table-cell">Issues</th><th className="px-2 sm:px-4 py-3 font-medium text-right hidden md:table-cell">Devs</th><th className="px-2 sm:px-4 py-3 font-medium text-right hidden lg:table-cell">Repos</th><th className="px-2 sm:px-4 py-3 font-medium text-right hidden lg:table-cell">Stars</th></tr></thead><tbody>{sorted.map((token_0, i) => {
            const totalStars = token_0.githubStats.reduce(_temp13, 0);
            return <tr key={token_0.id} className="border-b border-[hsl(var(--border))]/50 hover:bg-white/[0.03] transition-colors cursor-pointer" onClick={() => setSelectedToken(selectedToken?.id === token_0.id ? null : token_0)}><td className="px-2 sm:px-4 py-3 text-gray-600">{i + 1}</td><td className="px-2 sm:px-4 py-3 max-w-[150px] sm:max-w-none"><div className="flex items-center gap-2 sm:gap-3 min-h-[44px]">{token_0.imageUrl ? <Image src={token_0.imageUrl} alt={token_0.name} width={28} height={28} className="rounded-full shrink-0 w-6 h-6 sm:w-7 sm:h-7" /> : <div className="w-6 h-6 sm:w-7 sm:h-7 shrink-0 rounded-full bg-gray-700 flex items-center justify-center text-xs font-bold">{token_0.symbol.charAt(0)}</div>}<div className="min-w-0"><span className="font-medium text-white block sm:inline truncate">{token_0.name}</span><span className="sm:ml-2 text-gray-500 text-[11px] sm:text-xs block sm:inline truncate">{token_0.githubOrg}</span></div></div></td><td className="px-2 sm:px-4 py-3 text-right"><span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] sm:text-xs font-bold bg-blue-500/10 text-blue-400">{token_0.devActivityScore.toFixed(1)}</span></td><td className="px-2 sm:px-4 py-3 text-right font-mono text-white whitespace-nowrap">{token_0.totalCommits30d.toLocaleString()}</td><td className="px-2 sm:px-4 py-3 text-right font-mono text-cyan-400 whitespace-nowrap hidden sm:table-cell">{token_0.totalPRs30d.toLocaleString()}</td><td className="px-2 sm:px-4 py-3 text-right font-mono text-gray-400 whitespace-nowrap hidden md:table-cell">{token_0.totalIssues30d.toLocaleString()}</td><td className="px-2 sm:px-4 py-3 text-right font-mono text-purple-400 hidden md:table-cell">{token_0.activeDevs30d}</td><td className="px-2 sm:px-4 py-3 text-right font-mono text-gray-500 hidden lg:table-cell">{token_0.githubStats.length}</td><td className="px-2 sm:px-4 py-3 text-right font-mono text-yellow-400 hidden lg:table-cell">{totalStars.toLocaleString()}</td></tr>;
          })}</tbody></table></div>;
    t9 = viewMode === "chart" && <div className="rounded-xl border border-[hsl(var(--border))] bg-white/[0.02] p-4 sm:p-6"><DevActivityBarChart data={sorted.map(_temp14)} /></div>;
    $[0] = selectedToken?.id;
    $[1] = sortBy;
    $[2] = tokens;
    $[3] = viewMode;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  let t10;
  if ($[64] !== selectedToken) {
    t10 = selectedToken && <div className="mt-6 rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 sm:p-6"><div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6"><div className="flex items-center gap-3 min-w-0">{selectedToken.imageUrl && <Image src={selectedToken.imageUrl} alt={selectedToken.name} width={40} height={40} className="rounded-full" />}<div className="min-w-0"><h3 className="text-lg sm:text-xl font-bold truncate">{selectedToken.name}</h3><p className="text-xs sm:text-sm text-gray-400 truncate">github.com/{selectedToken.githubOrg}</p></div></div><div className="flex items-center gap-3 shrink-0"><DevScoreRadial score={selectedToken.devActivityScore} size={80} /><Link href={`/token/${selectedToken.slug}`} className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium transition-colors">View Token</Link></div></div>{selectedToken.devActivityHistory.length > 0 && <div className="mb-6"><CommitHeatmap weeks={Array.from({
          length: 52
        }, (_, i_0) => {
          const snapshot = selectedToken.devActivityHistory[i_0 % selectedToken.devActivityHistory.length];
          return snapshot ? snapshot.commits : 0;
        })} label="Commit Activity" /></div>}<div><h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">Repositories ({selectedToken.githubStats.length})</h4><div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{selectedToken.githubStats.map(_temp15)}</div></div></div>;
    $[64] = selectedToken;
    $[65] = t10;
  } else {
    t10 = $[65];
  }
  let t11;
  if ($[66] !== t1 || $[67] !== t10 || $[68] !== t2 || $[69] !== t3 || $[70] !== t4 || $[71] !== t5 || $[72] !== t6 || $[73] !== t7 || $[74] !== t8 || $[75] !== t9) {
    t11 = <div className={t1}>{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}</div>;
    $[66] = t1;
    $[67] = t10;
    $[68] = t2;
    $[69] = t3;
    $[70] = t4;
    $[71] = t5;
    $[72] = t6;
    $[73] = t7;
    $[74] = t8;
    $[75] = t9;
    $[76] = t11;
  } else {
    t11 = $[76];
  }
  return t11;
}
function _temp15(repo) {
  return <a key={repo.repoFullName} href={`https://github.com/${repo.repoFullName}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 p-4 min-h-[44px] rounded-xl bg-[hsl(230,15%,8%)] hover:bg-[hsl(230,15%,12%)] border border-[hsl(var(--border))] transition-colors group"><div className="min-w-0"><p className="font-medium text-blue-400 group-hover:text-blue-300 truncate transition-colors">{repo.repoFullName}</p><div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">{repo.language && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{
            backgroundColor: getLangColor(repo.language)
          }} />{repo.language}</span>}{repo.lastCommitAt && <span>Last commit: {new Date(repo.lastCommitAt).toLocaleDateString()}</span>}</div></div><div className="flex items-center gap-3 sm:gap-4 text-sm text-gray-400 shrink-0"><span className="flex items-center gap-1" title="Stars"><svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>{repo.stars.toLocaleString()}</span><span className="flex items-center gap-1" title="Forks"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>{repo.forks.toLocaleString()}</span></div></a>;
}
function _temp14(t_6) {
  return {
    name: t_6.symbol.toUpperCase(),
    slug: t_6.slug,
    commits: t_6.totalCommits30d,
    prs: t_6.totalPRs30d,
    devs: t_6.activeDevs30d,
    score: t_6.devActivityScore,
    imageUrl: t_6.imageUrl
  };
}
function _temp13(s_4, r_2) {
  return s_4 + r_2.stars;
}
function _temp12(lang_1) {
  return <span key={lang_1} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-white/5 text-gray-400"><span className="w-2 h-2 rounded-full" style={{
      backgroundColor: getLangColor(lang_1)
    }} />{lang_1}</span>;
}
function _temp11(r_1) {
  return r_1.language;
}
function _temp10(stat) {
  return <div key={stat.label} className="rounded-xl border border-[hsl(var(--border))] bg-white/[0.02] p-4 sm:p-5"><div className="flex items-center gap-2 mb-2"><svg className="w-4 h-4 shrink-0 text-blue-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d={stat.icon} /></svg><span className="text-[11px] sm:text-xs text-gray-500 uppercase tracking-wider leading-tight">{stat.label}</span></div><p className="text-xl sm:text-2xl font-bold text-white">{stat.value}</p></div>;
}
function _temp9(s_3, t_5) {
  return s_3 + t_5.activeDevs30d;
}
function _temp8(s_2, t0) {
  const [, c] = t0;
  return s_2 + c;
}
function _temp7(a_0, b_0) {
  return b_0[1] - a_0[1];
}
function _temp6(s_1, t_3) {
  return s_1 + t_3.githubStats.length;
}
function _temp5(t_2) {
  return t_2.githubStats.map(_temp4);
}
function _temp4(r) {
  return r.repoFullName.split("/")[0];
}
function _temp3(s_0, t_1) {
  return s_0 + t_1.totalPRs30d;
}
function _temp2(s, t_0) {
  return s + t_0.totalCommits30d;
}
function _temp(t) {
  return {
    name: t.symbol.toUpperCase(),
    slug: t.slug,
    commits: t.totalCommits30d,
    prs: t.totalPRs30d,
    devs: t.activeDevs30d,
    score: t.devActivityScore,
    imageUrl: t.imageUrl
  };
}